import { useCallback, useEffect, useState } from 'react';

import { Assignment } from 'shared/api/assignment-service';
import { ERROR_MESSAGE } from 'shared/config/constants';
import {
  useScheduledAssignments,
  filterBySubjects,
} from 'entities/assignments';
import { useAllClassesData } from 'entities/classes';
import { useFilters, useSelectedClassKey } from 'features/filters';
import { useAssignmentsQueryInvalidation } from 'features/assignment-panel';
import { QueryArguments } from 'widgets/stream-preview';

import { ITEMS_PER_SCROLL_COUNT, LOADING_MINIMAL_DURATION } from '../constants';

export const useInfiniteScheduledAssignments = () => {
  const [allFetchedAssignments, setAllFetchedAssignments] = useState<
    Assignment[] | null
  >(null);
  const [assignmentsToDisplay, setAssignmentsToDisplay] = useState<
    Assignment[] | null
  >(null);
  const [isAllAssignmentsShown, setIsAllAssignmentsShown] = useState(false);
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState(true);

  /*
   * consider loading completed when assignmentsToDisplay has been initialized
   * and only after some minimal duration (200ms)
   * to avoid loading spinner flickering when navigating from main page
   * */
  useEffect(() => {
    if (!assignmentsToDisplay) return;

    const timerId = setTimeout(() => {
      setIsAssignmentsLoading(false);
    }, LOADING_MINIMAL_DURATION);

    return () => clearTimeout(timerId);
  }, [assignmentsToDisplay]);

  const initialQueryArgs = {
    offset: 0,
    limit: ITEMS_PER_SCROLL_COUNT,
    selectedClass: [],
    subjects: [],
  };
  const [queryArgs, setQueryArgs] = useState<QueryArguments>(initialQueryArgs);

  const selectedClassKey = useSelectedClassKey();
  const { allClassesKeys } = useAllClassesData();
  const { selectedSubjects } = useFilters();

  const {
    scheduledAssignments,
    isLoading,
    isFetching,
    isError,
    refetch: refetchAssignments,
    ...rest
  } = useScheduledAssignments(queryArgs);

  const loadMoreAssignments = useCallback(() => {
    setQueryArgs((prevArgs) => ({
      ...prevArgs,
      offset: prevArgs.offset + ITEMS_PER_SCROLL_COUNT,
    }));
  }, []);

  const handleContentRefresh = () => {
    setQueryArgs((prevArgs) => {
      if (
        prevArgs.limit === prevArgs.limit + prevArgs.offset &&
        prevArgs.offset === 0
      ) {
        return prevArgs;
      }
      return {
        ...prevArgs,
        limit: prevArgs.limit + prevArgs.offset,
        offset: 0,
      };
    });

    // trigger fetching manually if query args won't be changed
    refetchAssignments();
  };

  useAssignmentsQueryInvalidation(handleContentRefresh);

  // store assignments from all performed queries
  useEffect(() => {
    if (!scheduledAssignments) return;

    if (queryArgs.offset === 0) {
      setAllFetchedAssignments(scheduledAssignments);
    } else {
      setAllFetchedAssignments((prevAssignments) => {
        const prev = prevAssignments ? [...prevAssignments] : [];

        return [...prev, ...scheduledAssignments];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledAssignments]);

  // check if all assignments has been shown to prevent additional requests
  useEffect(() => {
    if (!scheduledAssignments) return;

    if (scheduledAssignments.length < queryArgs.limit) {
      setIsAllAssignmentsShown(true);
    } else {
      setIsAllAssignmentsShown(false);
    }
  }, [scheduledAssignments, queryArgs.limit]);

  // set selectedClass queryArg to currently picked class to fetch assignments from that class
  useEffect(() => {
    if (!selectedClassKey.length) return;

    setQueryArgs((prevArgs) => ({
      ...prevArgs,
      selectedClass: selectedClassKey,
      offset: 0,
      limit: ITEMS_PER_SCROLL_COUNT,
    }));
  }, [selectedClassKey]);

  // set selectedClass queryArg to all classes if user have only one class
  useEffect(() => {
    if (allClassesKeys.length === 1) {
      setQueryArgs((prevArgs) => ({
        ...prevArgs,
        selectedClass: allClassesKeys,
        offset: 0,
        limit: ITEMS_PER_SCROLL_COUNT,
      }));
    }
  }, [allClassesKeys]);

  // filter all fetched assignments by subject
  useEffect(() => {
    const filteredScheduledAssignments = filterBySubjects(
      allFetchedAssignments,
      selectedSubjects,
    );

    setAssignmentsToDisplay(filteredScheduledAssignments);
  }, [selectedSubjects, allFetchedAssignments]);

  // trigger render of generic error screen on scheduled assignments page in case of request error
  useEffect(() => {
    if (!isError) return;

    throw new Error(ERROR_MESSAGE.ASSIGNMENTS_ERROR);
  }, [isError]);

  return {
    assignmentsToDisplay,
    isAllAssignmentsShown,
    loadMoreAssignments,
    isAssignmentsLoading,
    isAssignmentsFetching: isFetching,
    refetchScheduledAssignments: handleContentRefresh,
    currentClassesKeys: queryArgs.selectedClass,
    ...rest,
  };
};
